/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/naming-convention */
import { openToastr } from '@Common/handlers/services';
import { useLatestRef, useMountedRef, usePrevious, useSafeDispatch } from '@Common/hooks';
import { debounce } from '@quillbot/design-system';
import { useState, useEffect, Dispatch } from 'react';
import {
  ErrorCreatingProjectMsg,
  ErrorLoadingProjectListMsg,
} from 'src/researcher/constants/message.constants';
import {
  useApplicationDispatch,
  setTotalStorageUsedAction,
} from 'src/researcher/context/application';
import {
  useDialogDispatch,
  launchDialogAction,
  closeDialogAction,
} from 'src/researcher/context/ui/dialog';
import { ActiveDialogOnPageEnum } from 'src/researcher/context/ui/dialog.types';
import { listProjectsAPI } from 'src/researcher/handlers/api';
import researcherTrack from 'src/researcher/handlers/track';
import { AsyncStatusEnum, CreateProjectTriggerBy } from 'src/researcher/types/common';
import { ListProjectAPIResponse } from 'src/researcher/types/project';
import { Loadable } from 'src/researcher/component/common/Loadable';
import { useCreateProjectAndRoute } from 'src/researcher/hooks/useCreateProject';
import {
  resetStoreListStoreAction,
  setLoadingStatusListStoreAction,
  addProjectsListStoreAction,
  loadNextListStoreAction,
} from './list.reducer';
import { DSEIfSliderVisible } from './hooks';
import { resetDemoTemplates, setDemoTemplates, useTemplateDemoDispatch } from './template.store';
import { Loader } from './common';
import { useRecentTrashProjectDispatch, useRecentTrashProjectState } from './list.store';

export type LiveListDataLoadable = React.PropsWithChildren<any>;

export function LiveListDataLoadable(props: LiveListDataLoadable) {
  const listProjectsDispatch = useRecentTrashProjectDispatch();
  const applicationDispatch = useApplicationDispatch();
  const dialogDispatch = useDialogDispatch();
  const _templateDemoDispatch_ = useTemplateDemoDispatch();
  const safeTemplateDemoDispatch = useSafeDispatch(_templateDemoDispatch_ as Dispatch<any>);

  /**
   * query, skip can be changed to load data
   */
  const query = useRecentTrashProjectState((state) => state.recent.query);
  const skip = useRecentTrashProjectState((state) => state.recent.skip);
  const sort = useRecentTrashProjectState((state) => state.recent.sort);
  const forcedReloadCount = useRecentTrashProjectState((state) => state.recent.forceReloadCount);

  /**
   * Mounted ref needs to be be there to reset any state that already exist before mounting
   */
  const isMountedRef = useMountedRef();
  /**
   * Zombie store happens sometimes thus we reset store on mount
   */
  useEffect(() => {
    if (isMountedRef) {
      /**
       * Due to Zombie Store issue in redux we first clear store on every mount
       */
      listProjectsDispatch(resetStoreListStoreAction('recent'));
      safeTemplateDemoDispatch(resetDemoTemplates());
    }
  }, []);

  /**
   * during first loading
   * everything must be visible at once
   * This is because as there is create-new button on page
   * before loading the page we don't know storage used by the user
   * and if project creation is valid
   * so we must make list api call and if success show ui.
   * this ui must have used storage alert
   * */
  const [isLoadingFirstPage, setIsLoadingFirstPage] = useState(true);

  /**
   * create handler and route is simple router which routes after create-new api success
   */
  const createHandler = useCreateProjectAndRoute({
    onCatch: () => {
      /** The catch state must show a toast and close creating... dialog */
      openToastr(ErrorCreatingProjectMsg);
      dialogDispatch(closeDialogAction());
    },
  });

  useEffect(() => {
    listProjectsDispatch(setLoadingStatusListStoreAction(AsyncStatusEnum.PENDING, 'recent'));

    let ignore = false;
    const [sortBy, orderBy] = sort.split(':');
    listProjectsAPI({
      query,
      skip,
      limit: 16,
      status: 'live',
      sortBy,
      orderBy,
    })
      .then((data: ListProjectAPIResponse) => {
        if (ignore) {
          return;
        }
        /** set space used by all projects of this user */
        applicationDispatch(setTotalStorageUsedAction(data.totalStorageUsed));
        safeTemplateDemoDispatch(setDemoTemplates(data.templates));
        // total is 0, then it means that the user has not created any project yet
        // so we should show the create new project in background then update store and url query-param
        if (query === '' && data.total === 0) {
          // if project creation has failed previously
          // modal will reopen and get-projects call will be made again
          // as project = 0 so control will come here again
          // trying to create new project
          // and we may be stuck in this loop forever
          // if project creation failed previously then
          //  we should not try to create new project

          researcherTrack.createdNewProject(CreateProjectTriggerBy.NO_PROJECT);
          // @TODO: Somehow dialog is not visible here
          dialogDispatch(launchDialogAction(ActiveDialogOnPageEnum.CREATING_DIALOG));
          createHandler({ title: 'Untitled project', html: '' });
          listProjectsDispatch(setLoadingStatusListStoreAction(AsyncStatusEnum.RESOLVE, 'recent'));
          return;
        }
        // update the state
        // with the new projects list
        listProjectsDispatch(addProjectsListStoreAction(data, 'recent'));
      })
      .catch(() => {
        if (ignore) {
          return;
        }

        dialogDispatch(closeDialogAction());
        listProjectsDispatch(setLoadingStatusListStoreAction(AsyncStatusEnum.REJECT, 'recent'));
        openToastr(ErrorLoadingProjectListMsg);
      })
      .finally(() => {
        if (ignore) {
          return;
        }

        dialogDispatch(closeDialogAction());
        setIsLoadingFirstPage(false);
      });

    return () => {
      ignore = true;
    };
  }, [query, skip, sort, forcedReloadCount]);

  const _load_next_ = () => listProjectsDispatch(loadNextListStoreAction('recent'));
  const loadNextRef = useLatestRef(_load_next_);

  return (
    <Loadable loader={<Loader sliderId="none" />} loading={isLoadingFirstPage}>
      <DSEIfSliderVisible offset={150} sliderId="none" onScrollEnd={loadNextRef}>
        {props.children}
      </DSEIfSliderVisible>
    </Loadable>
  );
}
